import { useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import {
  bestaetigeNutzungsbedingungen,
  holeAnalyseSeitenDetails,
  holeBefundDetails,
  holeNutzungsbedingungen,
  holeText,
  holeVorschau,
  loescheEntwurf,
  sendeEntscheidung,
  sendeAnalyseSeitenEntscheidung,
  sendePdpEntscheidung,
  speichereEntwurf,
  starteLauf,
  starteVolleAnalyse,
} from './api/client.js';
import { AuthProvider } from './auth/auth-context.js';
import { firebaseAuthQuelle } from './auth/auth-source.js';
import { EnrollPage } from './auth/enroll-page.js';
import { firebaseEinrichtungsDienst } from './auth/enroll-service.js';
import { Guard } from './auth/guard.js';
import { LoginPage } from './auth/login-page.js';
import { firebasePasswortDienst } from './auth/passwort-service.js';
import { PasswortSetzenPage } from './auth/passwort-setzen-page.js';
import { PasswortVergessenPage } from './auth/passwort-vergessen-page.js';
import { firebaseAnmeldeDienst } from './auth/sign-in-service.js';
import { NewRunPage } from './forms/new-run-page.js';
import { RunFormRoute } from './forms/run-form-page.js';
import { AnalyseSeitePage } from './runs/analyse-seite-page.js';
import { AuftraegeProvider } from './runs/auftraege-kontext.js';
import { EntscheidungPage } from './runs/entscheidung-page.js';
import { ErgebnisPage } from './runs/ergebnis-page.js';
import { FreigabeRoute } from './runs/freigabe-route.js';
import type { OriginalQuelle } from './runs/originaltext-aufklapper.js';
import { RunDetailPage } from './runs/run-detail-page.js';
import { RunListPage } from './runs/run-list-page.js';
import { VorschauWeiterleitung } from './runs/vorschau-rahmen.js';
import { firestoreAuftragsQuelle } from './runs/runs-source.js';
import { WartendeProvider } from './runs/wartende-kontext.js';
import { Shell } from './ui/shell.js';

/** Originaltext der Review-Ansicht: derselbe Leseweg wie auf Entscheidungs- und Analyseseite. */
function holeOriginalDetails(quelle: OriginalQuelle) {
  return quelle.art === 'pdp'
    ? holeBefundDetails(quelle.runId)
    : holeAnalyseSeitenDetails(quelle.runId, quelle.auditId);
}

export function App() {
  // Einmal erzeugt und stabil gehalten: die Dienste tragen den Resolver bzw. das
  // TOTP-Geheimnis zwischen zwei Schritten. Ein neuer Dienst pro Renderdurchlauf wuerde sie
  // verlieren.
  const authQuelle = useMemo(firebaseAuthQuelle, []);
  const anmeldeDienst = useMemo(firebaseAnmeldeDienst, []);
  const einrichtungsDienst = useMemo(firebaseEinrichtungsDienst, []);
  const passwortDienst = useMemo(firebasePasswortDienst, []);
  const auftragsQuelle = useMemo(firestoreAuftragsQuelle, []);
  // Stabil, weil der Willkommensdialog bei jedem neuen Dienst neu nachfragt.
  const nutzung = useMemo(
    () => ({ holen: holeNutzungsbedingungen, bestaetigen: bestaetigeNutzungsbedingungen }),
    [],
  );

  return (
    <AuthProvider quelle={authQuelle}>
      <Routes>
        <Route path="/anmelden" element={<LoginPage dienst={anmeldeDienst} />} />
        {/* Ausserhalb des Guards: wer hier landet, hat (noch) kein Passwort oder hat es vergessen. */}
        <Route
          path="/passwort-vergessen"
          element={<PasswortVergessenPage dienst={passwortDienst} />}
        />
        <Route path="/passwort-setzen" element={<PasswortSetzenPage dienst={passwortDienst} />} />
        <Route
          path="/zweiter-faktor/einrichten"
          element={<EnrollPage dienst={einrichtungsDienst} />}
        />
        <Route
          element={
            <Guard>
              <WartendeProvider quelle={auftragsQuelle}>
                <AuftraegeProvider quelle={auftragsQuelle}>
                  <Shell nutzung={nutzung} />
                </AuftraegeProvider>
              </WartendeProvider>
            </Guard>
          }
        >
          <Route path="/" element={<RunListPage />} />
          <Route
            path="/auftraege/:runId"
            element={
              <RunDetailPage quelle={auftragsQuelle} starteVolleAnalyse={starteVolleAnalyse} />
            }
          />
          <Route
            path="/auftraege/:runId/ergebnis"
            element={
              <ErgebnisPage
                quelle={auftragsQuelle}
                holenVorschau={holeVorschau}
                starteVolleAnalyse={starteVolleAnalyse}
              />
            }
          />
          <Route path="/auftraege/:runId/vorschau" element={<VorschauWeiterleitung />} />
          <Route
            path="/auftraege/:runId/freigabe"
            element={
              <FreigabeRoute
                quelle={auftragsQuelle}
                holen={holeText}
                senden={sendeEntscheidung}
                speichereEntwurf={speichereEntwurf}
                loescheEntwurf={loescheEntwurf}
                holeOriginal={holeOriginalDetails}
              />
            }
          />
          <Route
            path="/auftraege/:runId/entscheidung"
            element={
              <EntscheidungPage
                quelle={auftragsQuelle}
                holen={holeBefundDetails}
                entscheide={sendePdpEntscheidung}
              />
            }
          />
          <Route
            path="/auftraege/:runId/seiten/:auditId"
            element={
              <AnalyseSeitePage
                quelle={auftragsQuelle}
                holen={holeAnalyseSeitenDetails}
                entscheide={sendeAnalyseSeitenEntscheidung}
              />
            }
          />
          <Route path="/neu" element={<NewRunPage />} />
          <Route path="/neu/:typ" element={<RunFormRoute starte={starteLauf} />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
